import React, { useState } from "react";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Textarea,
  Tooltip,
  makeStyles,
  Text,
  Field,
  Spinner,
  Toast,
  ToastTitle,
} from "@fluentui/react-components";
import { Connector16Regular } from "@fluentui/react-icons";
import AdminService from "../AdminService";

const useStyles = makeStyles({
  dialogSurface: {
    width: "600px",
    maxWidth: "90vw",
  },

  content: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: "8px",
  },

  input: {
    width: "100%",
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "16px",
  },

  helperText: {
    opacity: 0.7,
  },
  // Tooltip styles
  tooltipContent: {
    maxWidth: "420px",
    wordBreak: "break-all",
    whiteSpace: "pre-wrap",
    fontSize: "12px",
  },
});

export default function EditAcsDialog({ row, onSuccess, dispatchToast }) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [currentMaskedAcs, setCurrentMaskedAcs] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const validateConnectionString = (val) => {
    if (!val) return null;
    const parts = val.split(";").filter(Boolean);
    const hasEndpoint = parts.some((p) => /^endpoint=https:\/\/.+/i.test(p.trim()));
    const hasAccessKey = parts.some((p) => /^accesskey=.+/i.test(p.trim()));
    if (!hasEndpoint && !hasAccessKey)
      return "Must contain 'endpoint=https://...' and 'accesskey=...' separated by semicolons.";
    if (!hasEndpoint)
      return "Missing 'endpoint=https://...' in the connection string.";
    if (!hasAccessKey)
      return "Missing 'accesskey=...' in the connection string.";
    return null;
  };

  const validationError = isDirty ? validateConnectionString(value) : null;

  const handleOpen = async () => {
    setIsDirty(false);
    setValue("");
    setCurrentMaskedAcs("");
    setOpen(true);

    if (!row?.tenantId) return;

    try {
      setFetching(true);
      const res = await AdminService.getTenantAcs(row.tenantId);
      const masked = res?.data?.acsConnectionString || "";
      setCurrentMaskedAcs(masked);
      setValue(masked);
    } catch (err) {
      console.error("Failed to fetch ACS connection:", err);
      dispatchToast?.(
        <Toast>
          <ToastTitle>
            {err?.message || "Failed to load ACS connection"}
          </ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      await AdminService.editACSConnection({
        "tenantId": row?.tenantId,
        "config": {
          "acs": {
            "connectionString": value
          }
        }
      });

      dispatchToast(
        <Toast>
          <ToastTitle>
            ACS Connection updated successfully for {row?.tenantName}
          </ToastTitle>
        </Toast>,
        { intent: "success" }
      );

      setOpen(false);
      onSuccess?.();
    } catch (err) {
      console.error("Failed to update ACS:", err);

      dispatchToast(
        <Toast>
          <ToastTitle>
            {err?.message || "Failed to update ACS connection"}
          </ToastTitle>
        </Toast>,
        { intent: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip
        relationship="label"
        content="Edit ACS Connection"
      >
        <Button
          size="small"
          appearance="transparent"
          icon={<Connector16Regular />}
          onClick={handleOpen}
        />
      </Tooltip>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>Edit ACS Connection String</DialogTitle>

            <DialogContent className={styles.content}>
              <Text className={styles.helperText}>
                {`Update the Azure connection string for ${row?.tenantName} tenant.`}
              </Text>

              <Field
                label="ACS Connection String"
                required
                validationState={validationError ? "error" : isDirty && value ? "success" : "none"}
                validationMessage={
                  fetching
                    ? "Loading current connection string..."
                    : validationError || (isDirty && value ? "Valid connection string" : currentMaskedAcs ? "Showing currently saved connection string" : undefined)
                }
              >
                <Textarea
                  className={styles.input}
                  value={value}
                  rows={4}
                  onChange={(e, data) => { setValue(data.value); setIsDirty(true); }}
                  placeholder={fetching ? "Loading..." : "endpoint=https://example.communication.azure.com/;accesskey=yourkey"}
                  disabled={fetching}
                />
              </Field>
            </DialogContent>

            <DialogActions className={styles.footer}>
              <Button
                appearance="secondary"
                onClick={() => setOpen(false)}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                appearance="primary"
                onClick={handleSave}
                disabled={loading || fetching || !isDirty || !value || !!validationError}
                icon={loading ? <Spinner size="tiny" /> : null}
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}